import { useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'

export type Direction = 'asc' | 'desc'

/**
 * Click-to-sort table headers, with the state in the URL like every other
 * filter here.
 *
 * Every sortable column carries a dim arrow, not just the active one —
 * otherwise a sortable header is indistinguishable from a fixed one until you
 * happen to click it.
 */
export function useSort<T extends string>(keys: readonly T[], fallback: T) {
  const [params, setParams] = useSearchParams()

  const raw = params.get('sort') as T | null
  const sort = raw && keys.includes(raw) ? raw : fallback
  const direction: Direction = params.get('dir') === 'asc' ? 'asc' : 'desc'

  const sortBy = useCallback(
    (key: T) => {
      const next = new URLSearchParams(params)
      if (key === sort) {
        // Same column: flip the direction.
        const flipped = direction === 'desc' ? 'asc' : 'desc'
        if (flipped === 'desc') next.delete('dir')
        else next.set('dir', flipped)
      } else {
        // New column: start from the most useful end, which for every column
        // here (money, counts, recency) is the largest first.
        if (key === fallback) next.delete('sort')
        else next.set('sort', key)
        next.delete('dir')
      }
      next.delete('offset')
      setParams(next, { replace: true })
    },
    [params, setParams, sort, direction, fallback],
  )

  return { sort, direction, sortBy }
}

export function SortHeader<T extends string>({
  label,
  sortKey,
  sort,
  direction,
  onSort,
  numeric = false,
}: {
  label: string
  sortKey: T
  sort: T
  direction: Direction
  onSort: (key: T) => void
  numeric?: boolean
}) {
  const active = sort === sortKey
  return (
    <th
      className={`${numeric ? 'num ' : ''}sortable${active ? ' active' : ''}`}
      onClick={() => onSort(sortKey)}
      // Sorting is a control, so it answers to the keyboard as well as a click.
      tabIndex={0}
      role="button"
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          onSort(sortKey)
        }
      }}
      aria-sort={active ? (direction === 'asc' ? 'ascending' : 'descending') : 'none'}
      title={`Sort by ${label.toLowerCase()}`}
    >
      {label}
      <span className="arrow">{active ? (direction === 'desc' ? '▼' : '▲') : '↕'}</span>
    </th>
  )
}
